import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import CpfInput from './CpfInput';
import AddressForm from './AddressForm';
import Select from 'react-select';
import './AdminUserForm.module.scss';
import FormLabel from './FormLabel';
import PhoneNumberInput from './PhoneNumberInput';
import { alertModal } from '../utils/modalController';
import styles from './AdminUserForm.module.scss';
import { fetchAuth } from '../utils/fetchAuth';

export type AdminUserFormProps = {
  user?: User;
};

const apiUrl = process.env.REACT_APP_API_URL || '';

const AdminUserForm: React.FC<AdminUserFormProps> = ({ user }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [sexos, setSexos] = useState<Sexo[]>([]);
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [indication, setIndication] = useState<User[]>([]);
  const [civilStatus, setCivilStatus] = useState<CivilStatus[]>([]);

  const [users, setUsers] = useState<User[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<number[]>([]);
  const [selectedSexo, setSelectedSexo] = useState<number | null>(null);
  const [selectedRegistro, setSelectedRegistro] = useState<string | null>(null);
  const [selectedProfissional, setSelectedProfissional] = useState<number | null>(null);
  const [selectedIndicacao, setSelectedIndicacao] = useState<number | null>(null);
  const [selectedResponsavel, setSelectedResponsavel] = useState<number | null>(null);
  const [selectedCivilStatus, setSelectedCivilStatus] = useState<number | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [userAge, setUserAge] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true); // <-- Começa carregamento

      try {
        const [resPerms, resSexos, resRegs, resProfs, resUsers, resCivilStatus] = await Promise.all([
          fetch(apiUrl + '/contas/permissions'),
          fetch(apiUrl + '/contas/sexos'),
          fetch(apiUrl + '/contas/registros'),
          fetchAuth(apiUrl + '/admin/professionals'),
          fetchAuth(apiUrl + '/admin/persons'),
          fetch(apiUrl + '/contas/estados-civis'),
        ]);

        if (!resPerms.ok) {
          const data = await resPerms.json();
          throw new Error(data.message || 'Erro ao carregar permissões');
        }

        if (!resSexos.ok) {
          const data = await resSexos.json();
          throw new Error(data.message || 'Erro ao carregar sexos');
        }

        if(!resRegs.ok) {
          const data = await resRegs.json();
          throw new Error(data.message || 'Erro ao carregar registros');
        }

        if(!resCivilStatus.ok) {
          const data = await resCivilStatus.json();
          throw new Error(data.message || 'Erro ao carregar estados civis');
        }

        const [dataPerms, dataSexos, dataRegs, dataProfs, dataUsers, dataCivilStatus] = await Promise.all([
          resPerms.json(), resSexos.json(), resRegs.json(), resProfs.json(), resUsers.json(), resCivilStatus.json()
        ]);

        setPermissions(dataPerms);
        setSexos(dataSexos);
        setRegistros(dataRegs);
        setProfessionals(dataProfs);
        setUsers(dataUsers.data);
        setCivilStatus(dataCivilStatus);

        if (user) {
          const resUser = await fetchAuth(`${apiUrl}/admin/users/${user.id}`);
          if (!resUser.ok) {
            const errorData = await resUser.json();
            throw new Error(errorData.message || "Erro ao buscar usuário");
          }

          const dataUser : User = await resUser.json();
          let professional: Professional | undefined;
          let patient: Patient | undefined;

          if(dataUser.professional) {
            const resProfessional = await fetchAuth(`${apiUrl}/admin/professionals/${dataUser.professional.id}`);
            if (!resProfessional.ok) {
              const errorData = await resProfessional.json();
              throw new Error(errorData.message || "Erro ao buscar profissional");
            }
            professional = await resProfessional.json();
          }

          if(dataUser.patient) {
            const resPatient = await fetchAuth(`${apiUrl}/admin/patients/${dataUser.patient.id}`);
            if (!resPatient.ok) {
              const errorData = await resPatient.json();
              throw new Error(errorData.message || "Erro ao buscar paciente");
            }
            patient = await resPatient.json();
          }

          setUserData({
            ...dataUser,
            professional: professional,
            patient: patient
          });
          setSelectedPerms((dataPerms as Permission[])?.filter(
            p => (((dataUser as User)?.permission_level ?? 0) & p.code) !== 0
          )?.map(p => p.code));
          const matchedSexo = (dataSexos as Sexo[]).find(s => s.code === dataUser.sexo);
          setSelectedSexo(matchedSexo?.code ?? null);
          const matchedRegistro = (dataRegs as Registro[]).find(
            r => r.code === professional?.registro
          );
          setSelectedRegistro(matchedRegistro?.code ?? null);
          const matchedProfissional = (dataProfs as Professional[]).find(
            p => p.id === patient?.professional_responsible?.id
          )?.id;
          setSelectedProfissional(matchedProfissional ?? null);
          setUserAge(dataUser?.birthday ? Math.floor((new Date().getTime() - new Date(dataUser.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
          setSelectedResponsavel(patient?.legal_responsible ?? null);
          setSelectedCivilStatus(dataUser?.civil_status ?? null);
        } else {
          setUserData(null);
          setSelectedPerms([]);
          setSelectedSexo(null);
          setSelectedRegistro(null);
          setSelectedProfissional(null);
          setUserAge(null);
          setSelectedResponsavel(null);
          setSelectedCivilStatus(null);
        }
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        const errorString = typeof err === 'object'
          ? JSON.stringify(err, Object.getOwnPropertyNames(err))
          : String(err);
        alertModal(errorString);
        setPermissions([]);
      } finally {
        setIsLoading(false); // <-- Termina carregamento
      }
    };

    fetchAll();
  }, [user]);

  useEffect(() => {
    if (selectedProfissional) {
      const fetchPatients = async () => {
        try {
          const resPatients = await fetchAuth(apiUrl + `/admin/professionals/${selectedProfissional}/patients`);
          const dataPatients = await resPatients.json();
          setIndication(dataPatients);
          setSelectedIndicacao(userData?.patient?.indicacao ?? null);
        } catch (err) {
          console.error('Erro ao carregar pacientes:', err);
          alertModal('Erro ao carregar pacientes: ' + err);
        }
      };
      fetchPatients();
    }
  }, [selectedProfissional, userData]);

  return (
    <>
      <Form.Control type="hidden" name="id" id="id" defaultValue={userData?.id} />
      <Form.Group className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Group>
              <FormLabel required={true} htmlFor='nome'>Nome</FormLabel>
              <Form.Control
                name="nome"
                id='nome'
                className={styles['input-control']}
                defaultValue={isLoading ? 'Carregando...' : userData?.name}
                required
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <FormLabel required={true} htmlFor='cpf'>CPF:</FormLabel>
              <CpfInput
                required={true}
                id="cpf"
                className={`form-control ${styles['input-control']}`}
                defaultValue={isLoading ? 'Carregando...' : userData?.cpf}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='birthday'>Data de Nascimento:</FormLabel>
              <Form.Control
                name="birthday"
                id='birthday'
                className={styles['input-control']}
                type='date'
                defaultValue={isLoading ? '' : userData?.birthday}
                disabled={isLoading}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const age = Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365));
                  setUserAge(age);

                }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='civil_status'>Estado Civil:</FormLabel>
              <Select
                name="civil_status"
                id="civil_status"
                classNames={{
                  control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                  menu: () => styles['select-menu'],
                  option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                  container: () => styles['select-container'],
                }}
                options={civilStatus.map(s => ({
                  label: s.name,
                  value: s.code, // aqui é string, como "M" ou "F"
                }))}
                value={selectedCivilStatus ? { label: civilStatus.find(s => s.code === selectedCivilStatus)?.name || '', value: selectedCivilStatus } : null}
                onChange={(selected) => {
                  setSelectedCivilStatus(selected?.value ?? null)
                }}
                isDisabled={isLoading}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <FormLabel htmlFor='sexo-biologico'>Sexo biológico:</FormLabel>
              <Select
                name="sexo-biologico"
                id="sexo-biologico"
                classNames={{
                  control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                  menu: () => styles['select-menu'],
                  option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                  container: () => styles['select-container'],
                }}
                options={sexos.map(s => ({
                  label: s.name,
                  value: s.code, // aqui é string, como "M" ou "F"
                }))}
                value={selectedSexo ? { label: sexos.find(s => s.code === selectedSexo)?.name || '', value: selectedSexo } : null}
                onChange={(selected) => setSelectedSexo(selected?.value ?? null)}
                isDisabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Group>
              <FormLabel required={true} htmlFor='email'>E-mail:</FormLabel>
              <Form.Control
                name="email"
                type='email'
                id='email'
                className={styles['input-control']}
                defaultValue={isLoading ? 'Carregando...' : userData?.email}
                disabled={isLoading}
                required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='email-alternativo'>E-mail alternativo:</FormLabel>
              <Form.Control
                name="email-alternativo"
                id='email-alternativo'
                className={styles['input-control']}
                type="email"
                defaultValue={isLoading ? 'Carregando...' : userData?.email_alternativo}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='password'>Senha:</FormLabel>
              <Form.Control
                name="password"
                id='password'
                className={styles['input-control']}
                type='password'
                placeholder={userData?.have_password ? 'Deixe em branco para não alterar' : undefined}
                value={undefined} // Placeholder para o valor, se necessário
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <FormLabel htmlFor='password-confirm'>Confirme a senha:</FormLabel>
              <Form.Control
                name="password-confirm"
                id='password-confirm'
                className={styles['input-control']}
                type="password"
                placeholder={userData?.have_password ? 'Deixe em branco para não alterar' : undefined}
                value={undefined} // Placeholder para o valor, se necessário
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-3">
        <Row>
          <Col md>
            <Form.Group>
              <FormLabel id='personal-telefone'>Telefone:</FormLabel>
              <PhoneNumberInput
                id="personal-telefone"
                className={styles['input-control']}
                defaultValue={userData?.telefone ? `+${userData.telefone.ddi}${userData.telefone.numero}` : undefined}
                disabled={isLoading}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form.Group>

      <AddressForm
        id={"main"}
        defaultCep={userData?.address?.cep}
        defaultComplemento={userData?.address?.complemento}
        defaultNumero={userData?.address?.numero}
        disabled={isLoading}
      />

      <Form.Group className="mb-3">
        <FormLabel htmlFor='permissions'>Permissões:</FormLabel>
        <Select
          isMulti
          name='permissions[]'
          id='permissions'
          classNames={{
            control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
            menu: () => styles['select-menu'],
            option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
            container: () => styles['select-container'],
          }}
          options={permissions.map(p => ({ label: p.name, value: p.code }))}
          onChange={(values) => setSelectedPerms(values.map(v => v.value))}
          value={permissions.filter(p => selectedPerms.includes(p.code)).map(p => ({ label: p.name, value: p.code }))}
          isDisabled={isLoading}
        />
      </Form.Group>

      {selectedPerms?.some(perm => perm === permissions.find(
        p => p.name === 'Profissional'
      )?.code) && (
          <>
            <h4>Informações Profissionais</h4>
            <Form.Group className="mb-3">
              <FormLabel htmlFor='professional-name'>Nome:</FormLabel>
              <Form.Control
                name="professional-name"
                id='professional-name'
                className={styles['input-control']}
                defaultValue={isLoading ? 'Carregando...' : userData?.professional?.name}
                disabled={isLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <FormLabel htmlFor='registro'>Registro:</FormLabel>
                    <Select
                      name="registro"
                      classNames={{
                        control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                        menu: () => styles['select-menu'],
                        option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                        container: () => styles['select-container'],
                      }}
                      id="registro"
                      options={registros.map(r => ({ label: r.name, value: r.code }))}
                      onChange={(value) => setSelectedRegistro(value?.value ?? null)}
                      value={
                        selectedRegistro
                          ? { label: registros.find(r => r.code === selectedRegistro)?.name || '', value: selectedRegistro }
                          : null
                      }
                      isDisabled={isLoading}
                    />

                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <FormLabel htmlFor='registro-numero'>Número:</FormLabel>
                    <Form.Control
                      name="registro-numero"
                      id='registro-numero'
                      className={styles['input-control']}
                      defaultValue={isLoading ? 'Carregando...' : userData?.professional?.registro_numero}
                      disabled={isLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form.Group>
            <Form.Group className="mb-3">
              <Row>
                <Col md>
                  <FormLabel htmlFor='professional-telefone'>Telefone:</FormLabel>
                  <PhoneNumberInput
                    id="professional-telefone"
                    className={styles['input-control']}
                    defaultValue={userData?.professional?.telefone ? `+${userData.professional.telefone.ddi}${userData.professional.telefone.numero}` : undefined}
                    disabled={isLoading}
                  />
                </Col>
              </Row>
            </Form.Group>

            <AddressForm
              id={"professional"}
              defaultCep={userData?.professional?.address?.cep}
              defaultComplemento={userData?.professional?.address?.complemento}
              defaultNumero={userData?.professional?.address?.numero}
              disabled={isLoading}
            />
          </>
        )}

      {selectedPerms?.some(perm => perm === permissions.find(
        p => p.name === 'Paciente'
      )?.code) && (
          <>
            <h4>Informações do Paciente</h4>
            <Form.Group className="mb-3">
              <FormLabel htmlFor='professional-responsible'>Profissional Responsável:</FormLabel>
              <Select
                name="professional-responsible"
                id="professional-responsible"
                isSearchable={true}
                classNames={{
                  control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                  menu: () => styles['select-menu'],
                  option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                  container: () => styles['select-container'],
                }}
                options={professionals.map(p => ({ label: p.name || String(p.id), value: p.id }))}
                onChange={(value) => setSelectedProfissional(value?.value ?? null)}
                value={selectedProfissional ? { label: professionals.find(p => p.id === selectedProfissional)?.name || '', value: selectedProfissional } : null}
                isDisabled={isLoading}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FormLabel htmlFor='profissao' >Profissão:</FormLabel>
                  <Form.Control
                    name="profissao"
                    id='profissao'
                    className={styles['input-control']}
                    defaultValue={isLoading ? 'Carregando...' : userData?.patient?.profissao}
                    disabled={isLoading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FormLabel htmlFor='indicacao'>Indicação:</FormLabel>
                  <Select
                    name="indicacao"
                    id="indicacao"
                    classNames={{
                      control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                      menu: () => styles['select-menu'],
                      option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                      container: () => styles['select-container'],
                    }}
                    isSearchable={true}
                    options={indication.map(i => ({ label: i.name, value: i.id }))}
                    onChange={(values) => setSelectedIndicacao(values?.value ?? null)}
                    value={selectedIndicacao ? { label: indication.find(i => i.id === selectedIndicacao)?.name || '', value: selectedIndicacao } : null}
                    isDisabled={isLoading}
                  />
                </Form.Group>
              </Col>
            </Row>
            {(userAge ? userAge < 18 : false) && (
              <Form.Group className="mb-3">
                <FormLabel htmlFor='responsavel'>Responsável Legal:</FormLabel>
                <Select
                  name="responsavel"
                  id="responsavel"
                  isSearchable={true}
                  options={users.map(u => ({ label: u.name, value: u.id }))}
                  onChange={(value) => setSelectedResponsavel(value?.value ?? null)}
                  value={selectedResponsavel ? { label: users.find(u => u.id === selectedResponsavel)?.name || '', value: selectedResponsavel } : null}
                  isDisabled={isLoading}
                />
              </Form.Group>
            )}
          </>
        )}
    </>
  );
};

export default AdminUserForm;
